import { useState, type FormEvent, type ReactNode } from 'react';
import { api, errorMessage } from '../../lib/api';
import { goBack, Link, navigate, useSearchParams } from '../../lib/router';
import { useToast } from '../../state/toast';
import { Button, IconButton, TextField } from '../../components/ui/primitives';
import { Wordmark, LogoMark } from '../../components/ui/Logo';
import { LoginForm, RegisterForm } from './AuthForms';
import styles from './auth.module.css';

function AuthLayout({ title, subtitle, children, footer }: { title: string; subtitle?: string; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className={styles.page}>
      <div className={styles.topRow}>
        <IconButton icon="close" label="Sluiten" onClick={() => goBack('/')} />
      </div>
      <div className={styles.brand}>
        <LogoMark size={56} />
        <Wordmark size={26} />
      </div>
      <h1 className={styles.title}>{title}</h1>
      {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
      {children}
      {footer && <div className={styles.footer}>{footer}</div>}
    </div>
  );
}

export function LoginPage() {
  return (
    <AuthLayout
      title="Welkom terug"
      subtitle="Log in om te liken, reageren, volgen en playlists te maken."
      footer={
        <>
          Nog geen account? <Link to="/register">Registreren</Link>
        </>
      }
    >
      <LoginForm onDone={() => navigate('/', { replace: true })} onForgot={() => navigate('/forgot-password')} />
    </AuthLayout>
  );
}

export function RegisterPage() {
  return (
    <AuthLayout
      title="Maak je account"
      subtitle="Ontdek video's, volg creators en bouw je eigen playlists."
      footer={
        <>
          Al een account? <Link to="/login">Inloggen</Link>
        </>
      }
    >
      <RegisterForm onDone={() => navigate('/', { replace: true })} />
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/auth/password/forgot', { email });
      setSent(true);
    } catch (err) {
      toast(errorMessage(err), { tone: 'error' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout title="Wachtwoord vergeten" subtitle={sent ? undefined : 'We sturen je een link om een nieuw wachtwoord in te stellen.'} footer={<Link to="/login">Terug naar inloggen</Link>}>
      {sent ? (
        <p className={styles.success}>Als er een account bestaat voor {email}, ontvang je binnen een paar minuten een e-mail met een resetlink.</p>
      ) : (
        <form className={styles.form} onSubmit={submit}>
          <TextField label="E-mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
          <Button type="submit" size="lg" block loading={busy} disabled={!email}>
            Resetlink versturen
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const token = useSearchParams().get('token') ?? '';
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/auth/password/reset', { token, password });
      toast('Wachtwoord gewijzigd — log opnieuw in', { tone: 'success' });
      navigate('/login', { replace: true });
    } catch (err) {
      toast(errorMessage(err), { tone: 'error' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout title="Nieuw wachtwoord" subtitle="Kies een sterk wachtwoord van minimaal 8 tekens.">
      {!token ? (
        <p className={styles.error}>Deze link is ongeldig. Vraag een nieuwe resetlink aan.</p>
      ) : (
        <form className={styles.form} onSubmit={submit}>
          <TextField label="Nieuw wachtwoord" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" minLength={8} required />
          <Button type="submit" size="lg" block loading={busy} disabled={password.length < 8}>
            Wachtwoord opslaan
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
